class FarloUiRevolverCarousel {
	constructor(element, options = {}) {
		this.element = element;
		this.slides = Array.from(element.children);
		this.currentIndex = 0;
		this.startX = 0;
		this.dragStartX = 0;
		this.dragEndX = 0;
		this.visibleSlides = 1;
		this.totalVisibleSets = 0;
		this.isDragging = false;
		this.hasDragged = false;
		this.autoplayInterval = null;

		// Default options
		this.defaultOptions = {
			arrows: true,
			auto: true,
			autoDuration: 6000, // Set your default duration here
			fade: false,
			lazyLoad: false,
			loop: true,
			pagination: true,
			swipe: true
		};

		// Get options from data attributes
		const dataOptions = this.getDataOptions();
		this.options = { ...this.defaultOptions, ...dataOptions, ...options };

		this.handleKeyboardInput = this.handleKeyboardInput.bind(this);

		this.init();
	}

	getDataOptions() {
		const dataOptions = {};
		const auto = this.element.getAttribute('data-autoplay');
		const autoDuration = this.element.getAttribute('data-autoplay-duration');
		const mode = this.element.getAttribute('data-mode');
		const pagination = this.element.getAttribute('data-pagination');
		const arrows = this.element.getAttribute('data-arrows');

		if (auto !== null) {
			dataOptions.auto = auto === 'true';
		}
		if (autoDuration !== null) {
			dataOptions.autoDuration = parseInt(autoDuration, 10);
		}
		if (mode !== null) {
			dataOptions.mode = mode;
		}
		if (pagination !== null) {
			dataOptions.pagination = pagination === 'true';
		}
		if (arrows !== null) {
			dataOptions.arrows = arrows === 'true';
		}

		return dataOptions;
	}

	init() {
		if (this.element.classList.contains('farlo-ui-revolver-carousel--loaded')) {
			return;
		}
	
		this.track = document.createElement('div');
		this.track.className = 'farlo-ui-revolver-carousel__cylinder';
		this.element.classList.add('farlo-ui-revolver-carousel', 'farlo-ui-revolver-carousel--loaded');
	
		this.slides.forEach((slide) => {
			slide.classList.add('farlo-ui-revolver-carousel__chamber');
			this.track.appendChild(slide);
		});
	
		this.element.appendChild(this.track);
	
		this.calculateVisibleSlides();
		this.updateTotalVisibleSets();
		this.updateCarousel();
	
		if (this.options.pagination) {
			this.setupPagination(); // Setup the pagination with existing elements
		}
	
		if (this.options.arrows) {
			this.createArrows();
		}
	
		if (this.options.fade) {
			this.applyFadeEffect();
		}
	
		if (this.options.auto) {
			this.startAutoplay();
		}
	
		this.addEventListeners();
		this.preventLinkClicksDuringDrag();
	}

	setupPagination() {
		const paginationContainer = this.element.nextElementSibling;

		if (paginationContainer && paginationContainer.classList.contains('farlo-ui-revolver-carousel__pagination')) {
			const paginationImages = paginationContainer.querySelectorAll('.farlo-ui-revolver-carousel__pagination-img');

			paginationImages.forEach((img, index) => {
				img.addEventListener('click', () => {
					this.goToSlide(index);
				});
			});

			this.updatePaginationImages(); // Initial update to highlight the first slide
		}
	}

	updatePaginationImages() {
		const paginationContainer = this.element.nextElementSibling;
		if (paginationContainer && paginationContainer.classList.contains('farlo-ui-revolver-carousel__pagination')) {
			const images = paginationContainer.querySelectorAll('.farlo-ui-revolver-carousel__pagination-img');
			
			images.forEach((img, index) => {
				const isActive = index === this.currentIndex;
				img.classList.toggle('farlo-ui-revolver-carousel__pagination-img--active', isActive);
	
				// Scroll the pagination container to ensure the active thumbnail is visible
				if (isActive) {
					const containerRect = paginationContainer.getBoundingClientRect();
					const imgRect = img.getBoundingClientRect();
	
					// Check if the active image is out of view on the left side
					if (imgRect.left < containerRect.left) {
						paginationContainer.scrollLeft -= (containerRect.left - imgRect.left + imgRect.width);
					}
	
					// Check if the active image is out of view on the right side
					if (imgRect.right > containerRect.right) {
						paginationContainer.scrollLeft += (imgRect.right - containerRect.right + imgRect.width);
					}
				}
			});
		}
	}
	

	createArrows() {
		const prevArrow = document.createElement('button');
		prevArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--prev';
		prevArrow.setAttribute('aria-label', 'Previous Slide');
		prevArrow.innerHTML = '&lt;';
		prevArrow.addEventListener('click', () => {
			this.prevSlide();
			this.resetAutoplay();
		});

		const nextArrow = document.createElement('button');
		nextArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--next';
		nextArrow.setAttribute('aria-label', 'Next Slide');
		nextArrow.innerHTML = '&gt;';
		nextArrow.addEventListener('click', () => {
			this.nextSlide();
			this.resetAutoplay();
		});

		this.element.appendChild(prevArrow);
		this.element.appendChild(nextArrow);
	}

	resetAutoplay() {
		this.stopAutoplay();  // Stop the current autoplay
		this.startAutoplay(); // Restart autoplay with the full duration
	}

	calculateVisibleSlides() {
		if (this.slides.length > 0) {
			const carouselWidth = this.element.offsetWidth;
			const slideStyle = window.getComputedStyle(this.slides[0]);
			const slideWidth = this.slides[0].offsetWidth + parseInt(slideStyle.marginLeft, 10) + parseInt(slideStyle.marginRight, 10);
			this.visibleSlides = Math.round(carouselWidth / slideWidth);
		}
	}

	updateTotalVisibleSets() {
		this.totalVisibleSets = Math.ceil(this.slides.length / this.visibleSlides);
	}

	applyFadeEffect() {
		this.track.classList.add('farlo-ui-revolver-carousel__cylinder--smoke');
	}

	addEventListeners() {
		this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
		this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
		this.element.setAttribute('tabindex', '0');
		this.element.addEventListener('focus', () => {
			this.element.addEventListener('keydown', this.handleKeyboardInput);
		});
		this.element.addEventListener('blur', () => {
			this.element.removeEventListener('keydown', this.handleKeyboardInput);
		});

		if (this.options.swipe) {
			this.element.addEventListener('mousedown', this.startDrag.bind(this));
			this.element.addEventListener('mousemove', this.dragging.bind(this));
			this.element.addEventListener('mouseup', this.endDrag.bind(this));
			this.element.addEventListener('mouseleave', this.endDrag.bind(this));
		}

		window.addEventListener('resize', () => {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				this.onResize();
			}, 250);
		});
	}

	handleKeyboardInput(event) {
		switch (event.key) {
			case 'ArrowLeft':
				this.prevSlide();
				this.resetAutoplay();
				break;
			case 'ArrowRight':
				this.nextSlide();
				this.resetAutoplay();
				break;
		}
	}

	handleTouchStart(event) {
		this.startX = event.touches[0].clientX;
		this.stopAutoplay(); // Pause autoplay while dragging
	}

	handleTouchEnd(event) {
		const endX = event.changedTouches[0].clientX;
		if (this.startX - endX > 30) {
			this.nextSlide();
		} else if (this.startX - endX < -30) {
			this.prevSlide();
		}
		this.resetAutoplay(); // Reset autoplay after dragging ends
	}

	startDrag(event) {
		event.preventDefault();
		this.isDragging = true;
		this.hasDragged = false; // Reset hasDragged flag
		this.dragStartX = event.pageX;
		this.stopAutoplay(); // Pause autoplay while dragging
	}

	dragging(event) {
		if (!this.isDragging) {
			return;
		}
		this.dragEndX = event.pageX;
		if (Math.abs(this.dragStartX - this.dragEndX) > 5) {
			this.hasDragged = true;
		}
	}

	endDrag() {
		if (!this.isDragging) {
			return;
		}
		this.isDragging = false;
		const delta = this.dragStartX - this.dragEndX;
		if (this.hasDragged && Math.abs(delta) > 30) {
			delta > 0 ? this.nextSlide() : this.prevSlide();
		}
		this.resetAutoplay(); // Reset autoplay after dragging ends
	}

	onResize() {
		this.calculateVisibleSlides();
		this.updateTotalVisibleSets();
		if (this.currentIndex >= this.totalVisibleSets) {
			this.currentIndex = this.totalVisibleSets - 1;
		}
		this.updateCarousel();
	}

	goToSlide(index) {
		this.currentIndex = index;
		this.updateCarousel();
		this.resetAutoplay(); // Reset autoplay when a specific slide is selected via pagination
	}

	nextSlide() {
		this.currentIndex = this.currentIndex < this.totalVisibleSets - 1 ? this.currentIndex + 1 : (this.options.loop ? 0 : this.currentIndex);
		this.updateCarousel();
	}

	prevSlide() {
		this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : (this.options.loop ? this.totalVisibleSets - 1 : this.currentIndex);
		this.updateCarousel();
	}

	startAutoplay() {
		this.autoplayInterval = setInterval(() => {
			this.nextSlide();
		}, this.options.autoDuration);
	}

	stopAutoplay() {
		clearInterval(this.autoplayInterval);
	}

	updateCarousel() {
		// Calculate the transform index based on the current slide index
		const transformIndex = this.currentIndex * 100; // Move one slide at a time

		// Apply the transformation to the track to slide to the correct position
		this.track.style.transform = `translateX(-${transformIndex}%)`;

		// Update the active state and visibility of slides
		this.slides.forEach((slide, index) => {
			slide.classList.toggle('farlo-ui-revolver__chamber--active', index === this.currentIndex);
			slide.setAttribute('aria-hidden', index !== this.currentIndex);
			if (this.options.fade) {
				slide.style.opacity = this.options.fade && index === this.currentIndex ? '1' : '0';
			}
		});

		// Update the pagination to reflect the current slide
		this.updatePaginationImages();
	}

	preventLinkClicksDuringDrag() {
		this.slides.forEach((slide) => {
			const links = slide.querySelectorAll('a, button');
			links.forEach((link) => {
				link.addEventListener('click', (event) => {
					if (this.isDragging && !this.hasDragged) {
						event.preventDefault();
					}
				});
			});
		});
	}
}

export default FarloUiRevolverCarousel;
